/**
 * InvolvementResponseCard — "You've been involved".
 *
 * When the ball holder involves another level (catchball_involve_level) the
 * target is notified, and until now had nowhere on web to respond. This card
 * is the target's side: what was asked, a response box, and "Deliver response"
 * wired to the store's deliverInvolvement (catchball_deliver_involvement, 718).
 *
 * The delivered state shown here is the row the server returned — the store
 * only replaces the involvement after a successful RPC, so a refusal leaves
 * the card open with the server's text and the typed response intact.
 */

import { Users } from 'lucide-react';
import { useToast } from '../../../ui/Toast';
import { useCatchballTierTurnStore } from '../../../../stores/prioritization/catchballTierTurnStore';
import {
  INVOLVEMENT_MODE_LABELS,
  type CatchballTierInvolvement,
} from '../../../../types/prioritization/catchballTierTurn';
import { canDeliverInvolvement, involvementText } from './threadLogic';
import { ThreadComposer, formatWhen } from './ThreadComposer';

export function InvolvementResponseCard({
  involvement,
  viewerId,
  holderOrgUnitName,
  targetOrgUnitName,
}: {
  involvement: CatchballTierInvolvement;
  viewerId: string | null;
  holderOrgUnitName?: string;
  targetOrgUnitName?: string;
}) {
  const { showToast } = useToast();
  const deliverInvolvement = useCatchballTierTurnStore((s) => s.deliverInvolvement);
  const loadLoop = useCatchballTierTurnStore((s) => s.loadLoop);

  const gate = canDeliverInvolvement(involvement, viewerId);
  const asked = involvementText(involvement.requestContent);
  const delivered = involvement.state === 'delivered';
  const deliveredText = involvementText(involvement.deliveredContent);

  return (
    <section
      className={`border px-5 py-4 space-y-3 ${
        delivered
          ? 'border-[var(--rule)] bg-[var(--bg-raised)]'
          : 'border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)]/60 dark:bg-[var(--slate)]/15'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--text-pri)]">
            <Users className="h-4 w-4 text-[var(--text-ter)]" />
            You&rsquo;ve been involved
          </h2>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            {holderOrgUnitName ?? 'The ball holder'} involved {targetOrgUnitName ?? 'your level'} ·{' '}
            {INVOLVEMENT_MODE_LABELS[involvement.involvementMode] ?? involvement.involvementMode}
            {formatWhen(involvement.createdAt) && ` · ${formatWhen(involvement.createdAt)}`}
          </p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            delivered
              ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
              : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
          }`}
        >
          {delivered ? 'Delivered' : involvement.state === 'open' ? 'Waiting on you' : involvement.state}
        </span>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wide text-[var(--text-ter)]">What was asked</p>
        <p className="text-sm text-[var(--text-pri)] whitespace-pre-wrap mt-0.5">
          {asked || 'No written request came with this involvement.'}
        </p>
      </div>

      {delivered && (
        <div className="border-l-2 border-[var(--green)] bg-[var(--surface)] px-3 py-2">
          <p className="text-xs text-[var(--text-ter)]">
            Response delivered{formatWhen(involvement.deliveredAt) && ` · ${formatWhen(involvement.deliveredAt)}`}
          </p>
          {deliveredText && (
            <p className="text-sm text-[var(--text-sec)] whitespace-pre-wrap mt-1">{deliveredText}</p>
          )}
        </div>
      )}

      {!delivered && gate.allowed && viewerId && (
        <ThreadComposer
          label="Your response"
          placeholder="What the ball holder asked you for."
          submitLabel="Deliver response"
          busyLabel="Delivering…"
          onSubmit={async (text) => {
            const result = await deliverInvolvement({
              involvementId: involvement.id,
              // Same shape iOS writes and BallPositionPanel reads.
              outputContent: { description: text },
              actorId: viewerId,
            });
            if (!result.success) {
              throw new Error(result.error ?? 'The server refused the delivery.');
            }
            // Re-read so the delivered state on screen is the server's row.
            await loadLoop(involvement.loopId);
            showToast('success', 'Response delivered', 'The ball holder can now see it.');
          }}
        />
      )}

      {!delivered && !gate.allowed && gate.reason && (
        <p className="text-xs text-[var(--text-ter)]">{gate.reason}</p>
      )}
    </section>
  );
}
